// Version selection logic shared by every item detail page that renders
// a draft-or-latest view picker. Mirrors the original inline logic in
// `documents/hooks/useView.hook.tsx` (pre-literature); keep behavior
// identical across documents, inline-blocks, and literatures.

type THasId = { id: string };

type TApprovalLike = {
  versionId: string;
  status: string;
};

type TSelectVersionArgs<TDraft, TVersion extends THasId> = {
  draft: TDraft | undefined;
  versions: TVersion[];
  view: string | null;
  approvedVersionId?: string;
  enableApprovedView?: boolean;
  /** True when the current user has EDIT access or higher. */
  canEdit: boolean;
  /** True when the current user is in the AUTHOR group. */
  isAuthor: boolean;
};

export const resolveApprovedVersionId = <TVersion extends THasId>({
  versions,
  approvedVersionId,
  approvals = [],
}: {
  versions: TVersion[];
  approvedVersionId?: string;
  approvals?: TApprovalLike[];
}): string | undefined =>
  versions.find(
    (version) =>
      version.id === approvedVersionId ||
      approvals.some((approval) => approval.versionId === version.id && approval.status === 'APPROVED')
  )?.id;

export const selectVersion = <TDraft, TVersion extends THasId>({
  draft,
  versions,
  view,
  approvedVersionId,
  enableApprovedView,
  canEdit,
  isAuthor,
}: TSelectVersionArgs<TDraft, TVersion>): TDraft | TVersion | undefined => {
  let version: TDraft | TVersion | undefined = draft;

  if (view && (view === 'draft' || view === 'edit')) version = draft;
  else if (enableApprovedView && view === 'approved') {
    const approvedVersion = approvedVersionId ? versions.find((v) => v.id === approvedVersionId) : undefined;
    version = approvedVersion ?? versions[0];
  } else if (view && view !== 'latest') version = versions.find((v) => v.id === view);

  if (!version) {
    const latestVersion = versions[0];
    version = !latestVersion || (draft && (view === 'latest' || canEdit || !isAuthor)) ? draft : latestVersion;
  }

  return version;
};
